import { HardwareOptionInput } from '@/__generated__/graphql';
import { useSuspenseQuery } from '@apollo/client';
import ModalActions from '@components/modal/ModalActions';
import ModalTitle from '@components/modal/ModalTitle';
import { msg, t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogContent,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { Suspense, useMemo, useState, type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { getTranslatedHardwareOptionType } from '../data/hardwareOptionsTranslations';
import { GET_HARDWARE_OPTIONS } from '../graphql/queries/getHardwareOptions';
import { GET_IMAGE_VERSIONS } from '../graphql/queries/getHardwareImageVersions';

export type AdvancedFiltersValue = {
  hardwareOptions: HardwareOptionInput[];
  hardwareSerialNumbers: string[];
  hardwareImageVersions: string[];
  hardwareIccids: string[];
  hardwareImeis: string[];
};

interface Props {
  open: boolean;
  onClose: () => void;
  value: AdvancedFiltersValue;
  onSubmit: (value: AdvancedFiltersValue) => void;
}

type OptionsGroup = {
  type: string;
  options: {
    id: number;
    name: string;
    type: string;
  }[];
};

const FreeTextAutocomplete: FC<{
  value: string[];
  onChange: (value: string[]) => void;
  label: string;
  helperText: string;
}> = ({ value, onChange, label, helperText }) => {
  const [inputValue, setInputValue] = useState('');

  return (
    <Autocomplete
      multiple
      freeSolo
      value={value}
      options={[]}
      inputValue={inputValue}
      onChange={(_, value) => {
        const updatedValue = Array.from(
          new Set(value.flatMap((item) => item.split(/[\s,]+/).filter((x) => x.trim()))),
        );
        onChange(updatedValue);
      }}
      onInputChange={(_, newInputValue) => {
        const options = newInputValue.split(/[\s,]+/);
        if (options.length > 1) {
          const newValue = new Set([...value, ...options].map((x) => x.trim()).filter((x) => x));
          onChange(Array.from(newValue));
          setInputValue('');
        } else {
          setInputValue(newInputValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          onBlur={(event) => {
            const v = event.target.value.trim();
            if (v) {
              const newValue = new Set([...value, v].map((x) => x.trim()).filter((x) => x));
              onChange(Array.from(newValue));
            }
            setInputValue('');
          }}
          helperText={helperText}
        />
      )}
    />
  );
};

const Component: FC<Props> = (props) => {
  const { _, i18n } = useLingui();
  const { data } = useSuspenseQuery(GET_HARDWARE_OPTIONS);
  const { data: imageVersionsData } = useSuspenseQuery(GET_IMAGE_VERSIONS);

  const optionsGroups = useMemo(() => {
    const optionsGroups = data.hardwareOptions.reduce<OptionsGroup[]>((groups, option) => {
      const group = groups.find((group) => group.type === option.type);
      if (!group)
        groups.push({
          type: option.type,
          options: [option],
        });
      else group.options.push(option);
      return groups;
    }, []);

    optionsGroups.push({
      type: 'image_version',
      options: imageVersionsData.hardwareImageVersions.map((version, index) => ({
        id: index + 1, // ID ficticio para la UI
        name: version,
        type: 'image_version',
        value: version // Agregar el valor real
      }))
    });

    // Add null option for each group
    optionsGroups.forEach((group) =>
      group.options.push({ id: -1, name: t(i18n)`Nulo`, type: group.type }),
    );

    return optionsGroups;
  }, [data, i18n, imageVersionsData]);

  const methods = useForm<AdvancedFiltersValue>({
    defaultValues: {
      hardwareSerialNumbers: [],
      hardwareOptions: [],
      hardwareImageVersions: [],
      hardwareIccids: [],
      hardwareImeis: [],
    },
    values: {
      hardwareSerialNumbers: props.value.hardwareSerialNumbers,
      hardwareOptions: props.value.hardwareOptions,
      hardwareImageVersions: props.value.hardwareImageVersions,
      hardwareIccids: props.value.hardwareIccids,
      hardwareImeis: props.value.hardwareImeis,
    },
    resetOptions: {
      keepDefaultValues: true,
    },
  });

  const onSubmit = methods.handleSubmit((data) => {
    props.onSubmit({
      hardwareSerialNumbers: data.hardwareSerialNumbers,
      hardwareOptions: data.hardwareOptions.map((option) => ({
        id: option.id,
        type: option.type,
      })),
      hardwareImageVersions: data.hardwareImageVersions,
      hardwareIccids: data.hardwareIccids,
      hardwareImeis: data.hardwareImeis,
    });
    props.onClose();
  });

  const onCleanFilters = () => {
    methods.reset({
      hardwareOptions: [],
      hardwareSerialNumbers: [],
      hardwareImageVersions: [],
      hardwareIccids: [],
      hardwareImeis: [],
    } satisfies AdvancedFiltersValue);
  };

  return (
    <form onSubmit={onSubmit}>
      <DialogContent dividers sx={{ maxHeight: '60dvh' }}>
        <Box mb={4}>
          <Typography variant="h6" mb={2}>
            <Trans>Filtros por números de lote</Trans>
          </Typography>
          <Controller
            control={methods.control}
            name="hardwareSerialNumbers"
            render={({ field: { value, onChange } }) => (
              <SerialNumbersAutocomplete value={value} onChange={onChange} />
            )}
          />
        </Box>

        <Box mb={4}>
          <Typography variant="h6" mb={2}>
            <Trans>Filtros por 4G (ICCID / IMEI)</Trans>
          </Typography>
          <Controller
            control={methods.control}
            name="hardwareIccids"
            render={({ field: { value, onChange } }) => (
              <FreeTextAutocomplete
                value={value}
                onChange={onChange}
                label={_(msg`ICCID`)}
                helperText={_(msg`Ingresa uno o más ICCID. Puedes ingresar valores parciales.`)}
              />
            )}
          />
          <Box mt={2}>
            <Controller
              control={methods.control}
              name="hardwareImeis"
              render={({ field: { value, onChange } }) => (
                <FreeTextAutocomplete
                  value={value}
                  onChange={onChange}
                  label={_(msg`IMEI`)}
                  helperText={_(msg`Ingresa uno o más IMEI. Puedes ingresar valores parciales.`)}
                />
              )}
            />
          </Box>
        </Box>

        <Box>
          <Typography variant="h6" mb={2}>
            <Trans>Filtros por características</Trans>
          </Typography>
          <Controller
            control={methods.control}
            name="hardwareOptions"
            render={({ field: { value: selectedOptions, onChange } }) => {
              return (
                <Box display="grid">
                  {optionsGroups.filter(o => o.type !== 'image_version').map((group) => {
                    return (
                      <Box key={group.type} mb={2}>
                        <Typography fontWeight={600} mb={0.5}>
                          {getTranslatedHardwareOptionType(group.type, i18n)}
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {group.options.map((option) => {
                            const isSelected = selectedOptions.some(
                              (selectedOption) =>
                                selectedOption.id === option.id &&
                                selectedOption.type === option.type,
                            );
                            const onSelectOption = () => {
                              if (isSelected)
                                onChange(
                                  selectedOptions.filter(
                                    (selectedOption) => selectedOption.id !== option.id,
                                  ),
                                );
                              else onChange([...selectedOptions, option]);
                            };
                            return (
                              <Chip
                                key={option.type + option.id}
                                size="small"
                                label={option.name}
                                onClick={onSelectOption}
                                color={isSelected ? 'info' : 'default'}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            }}
          />
          <Controller
            control={methods.control}
            name="hardwareImageVersions"
            render={({ field: { value: selectedOptions, onChange } }) => {
              return (
                <Box display="grid">
                  {optionsGroups.filter(o => o.type === 'image_version').map((group) => {
                    return (
                      <Box key={group.type} mb={2}>
                        <Typography fontWeight={600} mb={0.5}>
                          {getTranslatedHardwareOptionType(group.type, i18n)}
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {group.options.map((option) => {
                            const isSelected = selectedOptions.includes(option.name)
                            const onSelectOption = () => {
                              if (isSelected)
                                onChange(
                                  selectedOptions.filter(
                                    (selectedOption) => selectedOption !== option.name,
                                  ),
                                );
                              else onChange([...selectedOptions, option.name]);
                            };
                            return (
                              <Chip
                                key={option.type + option.id}
                                size="small"
                                label={option.name}
                                onClick={onSelectOption}
                                color={isSelected ? 'info' : 'default'}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            }}
          />
        </Box>
      </DialogContent>
      <ModalActions
        onClose={props.onClose}
        onSubmit="submit"
        customButtons={[
          {
            label: _(msg`Limpiar filtros`),
            onClick: onCleanFilters,
            disabled: !methods.formState.isDirty,
          },
        ]}
        submitLabel={_(msg`Aplicar filtros`)}
      />
    </form>
  );
};

const SerialNumbersAutocomplete: FC<{
  value: string[];
  onChange: (value: string[]) => void;
}> = ({ value, onChange }) => {
  const { _ } = useLingui();
  const [serialNumbersInput, setSerialNumbersInput] = useState('');

  return (
    <Autocomplete
      multiple
      freeSolo
      value={value}
      options={[]}
      inputValue={serialNumbersInput}
      onChange={(_, value) => {
        const updatedValue = Array.from(
          new Set(value.flatMap((item) => item.split(/[\s,]+/).filter((x) => x.trim()))),
        );
        onChange(updatedValue);
      }}
      onInputChange={(_, newInputValue) => {
        const options = newInputValue.split(/[\s,]+/);
        if (options.length > 1) {
          const newValue = new Set([...value, ...options].map((x) => x.trim()).filter((x) => x));
          onChange(Array.from(newValue));
          setSerialNumbersInput('');
        } else {
          setSerialNumbersInput(newInputValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={_(msg`Números de lote`)}
          onBlur={(event) => {
            const inputValue = event.target.value.trim();
            if (inputValue) {
              const newValue = new Set(
                [...value, inputValue].map((x) => x.trim()).filter((x) => x),
              );
              onChange(Array.from(newValue));
            }
            setSerialNumbersInput('');
          }}
          helperText={_(
            msg`Ingresa una lista de números de lote. No es necesario que sean exactos; por ejemplo, al escribir "68", se mostrarán dispositivos con números de lote que contengan esa secuencia, como "236849".`,
          )}
        />
      )}
    />
  );
};

const Fallback: FC<Props> = (props) => {
  return (
    <>
      <DialogContent dividers sx={{ maxHeight: '60dvh' }}>
        <Skeleton height="100%" variant="rounded" />
      </DialogContent>
      <ModalActions onClose={props.onClose} />
    </>
  );
};

const AdvancedFiltersModal: FC<Props> = (props) => {
  const { _ } = useLingui();
  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="md">
      <ModalTitle title={_(msg`Filtros avanzados`)} onClose={props.onClose} />
      <Suspense fallback={<Fallback {...props} />}>
        <Component {...props} />
      </Suspense>
    </Dialog>
  );
};

export default AdvancedFiltersModal;
